
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  UserRole, 
  User,
  Company, 
  Order, 
  Product, 
  Client, 
  Team, 
  SalesGoal, 
  Lead, 
  LeadStatus,
  DriverPayment, 
  AppNotification, 
  Invoice, 
  Plan,
  Contract,
  Project,
  Estimate,
  Task
} from './types.ts';

import { SidebarItem, SidebarDropdown, ToastNotification } from './SharedComponents.tsx';
import { DashboardPage } from './Dashboard.tsx';
import { OrdersPage, OrderDetailsPanel } from './Orders.tsx';
import { CalendarPage } from './Calendar.tsx';
import { ClientsPage, ClientDetailsPanel, NewClientModal } from './Clients.tsx';
import { SettingsPage } from './Settings.tsx';
import { SeparationPage } from './Separation.tsx';
import { LogisticsMonitorPage } from './LogisticsMonitor.tsx';
import { QualityAnalysisPage } from './QualityAnalysis.tsx';
import { DriverPage } from './DriverView.tsx';
import { LoginPage } from './Login.tsx';
import { LandingPage } from './LandingPage.tsx';
import { TeamsPage } from './TeamsPage.tsx';
import { ProductsPage } from './Products.tsx';
import { MaintenancePage } from './Maintenance.tsx';
import { SuperAdminDashboard } from './SuperAdminDashboard.tsx';
import { GlobalFinance } from './GlobalFinance.tsx';
import { IntegrationCore } from './IntegrationCore.tsx';
import { NewCompanyModal } from './NewCompanyModal.tsx';
import { MasterLoginPage } from './MasterLoginPage.tsx';
import { ActivityLogPage } from './ActivityLogPage.tsx';
import { InvoicesPage } from './InvoicesPage.tsx';
import { ContractsPage } from './ContractsPage.tsx';
import { ProjectsPage } from './ProjectsPage.tsx';
import { TasksPage } from './TasksPage.tsx';
import { LeadsPage, LeadDetailsModal } from './LeadsPage.tsx';
import { GoalsPage } from './GoalsPage.tsx';
import { EventFinancePage } from './EventFinancePage.tsx';
import { EventProfitabilityPage } from './EventProfitabilityPage.tsx';
import { ExpeditionTerminalPage } from './ExpeditionTerminal.tsx';
import { CompaniesManagement } from './CompaniesManagement.tsx';
import { UserManagementPage } from './UserManagement.tsx';
import { EstimatesPage } from './Estimates.tsx';
import { PaymentsPage } from './PaymentsPage.tsx';
import { InternalFinancePage } from './InternalFinancePage.tsx';
import { NewTransactionModal } from './NewTransactionModal.tsx';
import { db, supabase, hasValidSupabaseConfig } from './supabase.ts';

import { 
  LayoutDashboard, 
  ClipboardList, 
  Calendar as CalendarIcon, 
  Users, 
  BarChart3, 
  Settings as SettingsIcon, 
  Package, 
  Radio, 
  ShieldCheck, 
  LogOut,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Building2,
  DollarSign,
  Zap,
  History,
  Target,
  FileText,
  FileSignature,
  FolderKanban,
  TrendingUp,
  Activity,
  CreditCard,
  Truck,
  Key,
  Wallet,
  PackageSearch,
  AlertTriangle,
  EyeOff,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

import { Globe as InteractiveGlobe } from './components/ui/interactive-globe';

function App() {
  const isAppDomain = useMemo(() => {
    const host = window.location.hostname;
    // Se estiver em localhost ou subdomínio app., mostra o login.
    if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('app.')) {
      return true;
    }
    // Se estiver no domínio principal da landing page, mostra a landing (retorna false).
    // Caso contrário (domínios de preview ou outros), mostra o login (retorna true).
    const isLandingDomain = host === 'eventlogpro.com.br' || host === 'www.eventlogpro.com.br';
    return !isLandingDomain;
  }, []);

  const isStandalone = useMemo(() => {
    return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  }, []);

  const [user, setUser] = useState<User | null>(null);
  
  useEffect(() => {
    console.log("[APP] User state changed:", user?.email || "null");
  }, [user]);

  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isMasterLogin, setIsMasterLogin] = useState(false);
  const [showLogin, setShowLogin] = useState(() => {
    const stored = localStorage.getItem('evl_show_login');
    if (stored !== null) return JSON.parse(stored);
    return isAppDomain || isStandalone;
  }); 

  useEffect(() => {
    localStorage.setItem('evl_show_login', JSON.stringify(showLogin));
  }, [showLogin]);
  const [showRegister, setShowRegister] = useState(false); 
  const [preSelectedPlan, setPreSelectedPlan] = useState<string | null>(null); 
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Modais de Cadastro
  const [isNewTxModalOpen, setIsNewTxModalOpen] = useState(false);
  const [isNewCompanyModalOpen, setIsNewCompanyModalOpen] = useState(false);
  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  
  const [impersonatedCompanyId, setImpersonatedCompanyId] = useState<string | null>(null);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    master: true,
    comercial: true,
    logistica: true,
    financeiro: true,
    sistema: true
  });

  const [orders, setOrders] = useState<Order[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users_list, setUsersList] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [kanbanColumns, setKanbanColumns] = useState<any[]>([]);
  const [goals, setGoals] = useState<SalesGoal[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [driverPayments, setDriverPayments] = useState<DriverPayment[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [estimates, setEstimates] = useState<Estimate[]>([]);

  const [isExpeditionTerminalOpen, setIsExpeditionTerminalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const syncUnitData = useCallback(async (userId: string, companyId?: string) => {
    const targetCompanyId = impersonatedCompanyId || companyId;
    
    setIsSyncing(true);
    setSyncError(null);
    try {
      const dbPlans = await db.getPlans();
      setPlans(dbPlans as any);

      if (!targetCompanyId) {
        console.log("[SYNC] No targetCompanyId, fetching all companies and users...");
        const [allCos, allUsr] = await Promise.all([
            db.getCompanies(),
            db.getUsers()
        ]);
        console.log("[SYNC] Fetched all:", allCos.length, "companies,", allUsr.length, "users");
        setCompanies(allCos as any);
        setUsersList(allUsr as any);
      } else {
        console.log("[SYNC] Fetching data for company:", targetCompanyId);
        const [cos, clis, ords, prods, tms, lds, usrs, pjs, ctrs, ests, invs, dps, kcols, tsks] = await Promise.all([
          db.getCompanies(),
          db.getClients(targetCompanyId),
          db.getOrders(targetCompanyId),
          db.getProducts(targetCompanyId),
          db.getTeams(targetCompanyId),
          db.getLeads(targetCompanyId),
          db.getUsers(targetCompanyId),
          db.getProjects(targetCompanyId),
          db.getContracts(targetCompanyId),
          db.getEstimates(targetCompanyId),
          db.getInvoices(targetCompanyId),
          db.getDriverPayments(targetCompanyId),
          db.getKanbanColumns(targetCompanyId),
          db.getTasks(targetCompanyId)
        ]);

        console.log("[SYNC] Fetched company data:", {
          companies: cos.length,
          clients: clis.length,
          orders: ords.length,
          users: usrs.length
        });

        setCompanies(cos as any);
        setClients(clis as any);
        setOrders(ords as any);
        setProducts(prods as any);
        setTeams(tms as any);
        setLeads(lds as any);
        setUsersList(usrs as any);
        setProjects(pjs as any);
        setContracts(ctrs as any);
        setEstimates(ests as any);
        setInvoices(invs as any);
        setDriverPayments(dps as any);
        setTasks(tsks as any);
        if (kcols && kcols.length > 0) setKanbanColumns(kcols);
      }
    } catch (e: any) {
      console.error("Erro na sincronização:", e);
      setSyncError(e.message || "Erro desconhecido ao sincronizar dados.");
    } finally {
      setIsSyncing(false);
    }
  }, [impersonatedCompanyId]);

  // Check session on mount
  useEffect(() => {
    const checkSession = async (retries = 3) => {
      console.log("[AUTH] Checking session...", { retries });
      
      // Tentar restaurar sessão mock primeiro
      const savedMockUser = localStorage.getItem('evl_mock_user');
      if (savedMockUser) {
        try {
          const parsed = JSON.parse(savedMockUser);
          console.log("[AUTH] Restoring mock session:", parsed.email);
          setUser(parsed);
          setActiveTab(parsed.role === UserRole.SUPER_ADMIN ? 'master-dash' : 'dashboard');
          setShowLogin(false);
          return;
        } catch (e) {
          localStorage.removeItem('evl_mock_user');
        }
      }

      if (!hasValidSupabaseConfig) return;

      try {
          // Timeout para o getSession (30 segundos)
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Timeout ao restaurar sessão")), 30000)
          );

          const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]) as any;
          
          if (error) {
            console.error("[AUTH] getSession error:", error.message);
            if (retries > 0 && (error.message.includes('schema') || error.message.includes('timeout'))) {
              console.log("[AUTH] Retrying session check...");
              setTimeout(() => checkSession(retries - 1), 2000);
              return;
            }
          }

          console.log("[AUTH] Session found:", !!session, "User:", session?.user?.email);
          if (session?.user) {
            console.log("[AUTH] User in session, fetching profile...");
            const profile = await db.getProfile(session.user.id);
            if (profile) {
              console.log("[AUTH] Profile found, setting user:", profile.email);
              setUser(profile as any);
              setActiveTab(profile.role === UserRole.SUPER_ADMIN ? 'master-dash' : 'dashboard');
              setShowLogin(false);
            } else {
              console.warn("[AUTH] Profile NOT found for user in session, using fallback.");
              const fallbackUser: User = {
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.email?.split('@')[0] || 'Usuário',
                role: UserRole.STAFF,
                active: true,
                createdAt: new Date().toISOString()
              };
              setUser(fallbackUser as any);
              setShowLogin(false);
            }
          }
      } catch (e: any) {
        console.error("[AUTH] Error restoring session:", e);
        if (retries > 0 && (e.message.includes('Timeout') || e.message.includes('schema'))) {
          console.log("[AUTH] Retrying session check after exception...");
          setTimeout(() => checkSession(retries - 1), 3000);
        }
      }
    };
    checkSession();

    let sub: any = null;
    if (hasValidSupabaseConfig) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log("[AUTH] Event:", event, "Session user:", session?.user?.email);
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          console.log("[AUTH] User signed in or initial session, fetching profile...");
          const profile = await db.getProfile(session.user.id);
          if (profile) {
            console.log("[AUTH] Profile found, setting user:", profile.email);
            setUser(profile as any);
            setActiveTab(profile.role === UserRole.SUPER_ADMIN ? 'master-dash' : 'dashboard');
            setShowLogin(false);
          } else {
            console.warn("[AUTH] Profile NOT found for user on event, using fallback.");
            const fallbackUser: User = {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.email?.split('@')[0] || 'Usuário',
              role: UserRole.STAFF,
              active: true,
              createdAt: new Date().toISOString()
            };
            setUser(fallbackUser as any);
            setShowLogin(false);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log("[AUTH] User signed out, clearing state");
          localStorage.removeItem('evl_user');
          setUser(null);
          setShowLogin(true);
          setLeads([]);
          setOrders([]);
          setClients([]);
          setInvoices([]);
          setContracts([]);
          setEstimates([]);
          setDriverPayments([]);
        }
      });
      sub = subscription;
    }

    return () => {
      if (sub) sub.unsubscribe();
    };
  }, []);

  // Real-time subscriptions
  useEffect(() => {
    if (!hasValidSupabaseConfig || !user) return;

    const targetCoId = impersonatedCompanyId || user.companyId;
    if (!targetCoId) return;

    const leadsChannel = supabase
      .channel('leads-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'leads',
        filter: `company_id=eq.${targetCoId}`
      }, async (payload) => {
        console.log("Real-time Lead change:", payload);
        const updatedLeads = await db.getLeads(targetCoId);
        setLeads(updatedLeads as any);
      })
      .subscribe((status) => {
        console.log("Leads subscription status:", status);
      });

    const ordersChannel = supabase
      .channel('orders-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'orders',
        filter: `company_id=eq.${targetCoId}`
      }, async () => {
        const updatedOrders = await db.getOrders(targetCoId);
        setOrders(updatedOrders as any);
      })
      .subscribe();

    const clientsChannel = supabase
      .channel('clients-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'clients',
        filter: `company_id=eq.${targetCoId}`
      }, async () => {
        const updatedClients = await db.getClients(targetCoId);
        setClients(updatedClients as any);
      })
      .subscribe();

    const columnsChannel = supabase
      .channel('columns-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'kanban_columns',
        filter: `company_id=eq.${targetCoId}`
      }, async () => {
        const updatedCols = await db.getKanbanColumns(targetCoId);
        if (updatedCols && updatedCols.length > 0) setKanbanColumns(updatedCols);
      })
      .subscribe();

    const contractsChannel = supabase
      .channel('contracts-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contracts', filter: `company_id=eq.${targetCoId}` }, async () => {
        const updated = await db.getContracts(targetCoId);
        setContracts(updated as any);
      }).subscribe();

    const estimatesChannel = supabase
      .channel('estimates-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimates', filter: `company_id=eq.${targetCoId}` }, async () => {
        const updated = await db.getEstimates(targetCoId);
        setEstimates(updated as any);
      }).subscribe();

    const invoicesChannel = supabase
      .channel('invoices-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices', filter: `company_id=eq.${targetCoId}` }, async () => {
        const updated = await db.getInvoices(targetCoId);
        setInvoices(updated as any);
      }).subscribe();

    const driverPaymentsChannel = supabase
      .channel('driver-payments-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'driver_payments', filter: `company_id=eq.${targetCoId}` }, async () => {
        const updated = await db.getDriverPayments(targetCoId);
        setDriverPayments(updated as any);
      }).subscribe();

    const tasksChannel = supabase
      .channel('tasks-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `company_id=eq.${targetCoId}` }, async () => {
        const updated = await db.getTasks(targetCoId);
        setTasks(updated as any);
      }).subscribe();

    const projectsChannel = supabase
      .channel('projects-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects', filter: `company_id=eq.${targetCoId}` }, async () => {
        const updated = await db.getProjects(targetCoId);
        setProjects(updated as any);
      }).subscribe();

    return () => {
      supabase.removeChannel(leadsChannel);
      supabase.removeChannel(ordersChannel);
      supabase.removeChannel(clientsChannel);
      supabase.removeChannel(columnsChannel);
      supabase.removeChannel(contractsChannel);
      supabase.removeChannel(estimatesChannel);
      supabase.removeChannel(invoicesChannel);
      supabase.removeChannel(driverPaymentsChannel);
      supabase.removeChannel(tasksChannel);
      supabase.removeChannel(projectsChannel);
    };
  }, [user, impersonatedCompanyId]);

  useEffect(() => {
    if (user) syncUnitData(user.id, user.companyId);
    else db.getPlans().then(p => setPlans(p as any));
  }, [user, impersonatedCompanyId, syncUnitData]);

  const currentCompany = useMemo(() => {
    const id = impersonatedCompanyId || user?.companyId;
    if (id) return companies.find(c => c.id === id);
    if (user?.role === UserRole.SUPER_ADMIN && companies.length > 0) return companies[0];
    return undefined;
  }, [companies, user, impersonatedCompanyId]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const addNotification = (title: string, message: string, type: 'info' | 'warning' | 'error' | 'success') => {
    const newNotif: AppNotification = {
      id: Math.random().toString(36).substr(2, 9),
      title,
      message,
      type,
      timestamp: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev]);
    // Auto remove after 8 seconds (matching ToastNotification timeout)
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== newNotif.id));
    }, 8000);
  };

  const handleLogin = (loggedUser: any) => {
    console.log("[APP] handleLogin called with user:", loggedUser.email);
    setUser(loggedUser);
    setIsMasterLogin(false);
    setShowLogin(false);
    setShowRegister(false);
    setActiveTab(loggedUser.role === UserRole.SUPER_ADMIN ? 'master-dash' : 'dashboard');
    
    // Persistir sessão mock se não for Supabase
    if (loggedUser.id === 'master-id' || loggedUser.id === 'demo-user-id' || loggedUser.id.startsWith('u-')) {
      localStorage.setItem('evl_mock_user', JSON.stringify(loggedUser));
    }
    
    console.log("[APP] user state set, showLogin set to false");
  };

  const handleLogout = async () => {
    // Limpar estado local primeiro para resposta imediata na UI
    localStorage.removeItem('evl_mock_user');
    setUser(null);
    setImpersonatedCompanyId(null);
    setShowLogin(true);
    setIsMasterLogin(false);
    localStorage.setItem('evl_show_login', 'true');

    // Tentar deslogar do Supabase em segundo plano
    try {
      if (hasValidSupabaseConfig) {
        await supabase.auth.signOut();
      }
    } catch (err) {
      console.warn("[AUTH] Error during signOut:", err);
    }
  };

  const handleManageLicense = async (id: string, updates: Partial<Company>) => {
    try {
      const updated = await db.updateCompany(id, updates as any);
      setCompanies(prev => prev.map(c => c.id === id ? updated as any : c));
      addNotification("Sucesso", "Licença atualizada!", "success");
    } catch (_e) { addNotification("Erro", "Erro ao atualizar licença.", "error"); }
  };

  const handleToggleBlock = async (id: string) => {
    const company = companies.find(c => c.id === id);
    if (!company) return;
    try {
      const updated = await db.updateCompany(id, { blocked: !company.blocked, status: company.blocked ? 'ACTIVE' : 'PAST_DUE' } as any);
      setCompanies(prev => prev.map(c => c.id === id ? updated as any : c));
      addNotification("Atenção", `Unidade ${company.blocked ? 'desbloqueada' : 'bloqueada'}.`, "info");
    } catch (_e) { addNotification("Erro", "Erro no bloqueio.", "error"); }
  };

  const handleToggleNfse = async (id: string) => {
    const company = companies.find(c => c.id === id);
    if (!company) return;
    try {
      const updated = await db.updateCompany(id, { nfseEnabled: !company.nfseEnabled } as any);
      setCompanies(prev => prev.map(c => c.id === id ? updated as any : c));
      addNotification("Sucesso", `NFS-e ${updated.nfseEnabled ? 'ativada' : 'desativada'}.`, "success");
    } catch (_e) { addNotification("Erro", "Erro NFS-e.", "error"); }
  };

  const handleDeleteCompany = async (id: string) => {
    if (!window.confirm("Remover permanentemente?")) return;
    try {
      await db.deleteCompany(id);
      setCompanies(prev => prev.filter(c => c.id !== id));
      addNotification("Sucesso", "Unidade removida.", "success");
    } catch (_e) { addNotification("Erro", "Erro ao remover.", "error"); }
  };

  const handleCreateCompany = async (c: Company, m: any) => {
    try {
      const savedCo = await db.createCompany(c as any);
      const savedUsr = await db.registerManager(m, savedCo.id);
      setCompanies(prev => [...prev, savedCo as any]);
      setUsersList(prev => [...prev, savedUsr as any]);
      
      if (user?.role !== UserRole.SUPER_ADMIN) handleLogin(savedUsr);
      
      setIsNewCompanyModalOpen(false);
      setShowRegister(false);
      addNotification("Sucesso", `Unidade '${savedCo.name}' provisionada.`, "success");
    } catch (e: any) { addNotification("Erro", `Falha: ${e.message}`, "error"); }
  };

  const handleCreateClient = async (client: Client) => {
    try {
      const targetCoId = impersonatedCompanyId || user?.companyId || (user?.role === UserRole.SUPER_ADMIN ? companies[0]?.id : null);
      if (!targetCoId) return;
      const { data, error } = await supabase.from('clients').insert({ ...client, company_id: targetCoId } as any).select().single();
      if (error) throw error;
      setClients(prev => [...prev, data as any]);
      setIsNewClientModalOpen(false);
      addNotification("Sucesso", "Cliente cadastrado!", "success");
    } catch (e: any) { addNotification("Erro", "Erro: " + e.message, "error"); }
  };

  const handleNewTransaction = (_tx: any) => {
    setIsNewTxModalOpen(false);
    addNotification("Sucesso", "Lançamento registrado.", "success");
  };

  const handleUpdateCompany = async (updates: Partial<Company>) => {
    const id = impersonatedCompanyId || user?.companyId || (user?.role === UserRole.SUPER_ADMIN ? companies[0]?.id : null);
    if (!id) {
      addNotification("Aviso", "Nenhuma unidade selecionada para atualização.", "warning");
      return;
    }
    try {
      const updated = await db.updateCompany(id, updates as any);
      setCompanies(prev => prev.map(c => c.id === id ? updated as any : c));
      addNotification("Sucesso", "Dados da unidade atualizados.", "success");
    } catch (_e) { addNotification("Erro", "Erro ao atualizar dados da unidade.", "error"); }
  };

  // Removida a tela de bloqueio de configuração para permitir que o "básico" funcione sempre.
  if (!user) {
    if (isMasterLogin) return <MasterLoginPage onLogin={handleLogin} onCancel={() => setIsMasterLogin(false)} />;
    if ((showLogin || isAppDomain || isStandalone) && !user) return (
      <LoginPage 
        onLogin={handleLogin} 
        onBack={(isStandalone || isAppDomain) ? undefined : () => setShowLogin(false)} 
      />
    );
    if (showRegister) return <NewCompanyModal onClose={() => setShowRegister(false)} onSubmit={handleCreateCompany} initialPlan={preSelectedPlan} />;
    return <LandingPage plans={plans} onLoginClick={() => setShowLogin(true)} onRegisterClick={(p) => { setPreSelectedPlan(p || 'PRO'); setShowRegister(true); }} onMasterAccess={() => setIsMasterLogin(true)} />;
  }

  if (user.role === UserRole.MONTAGEM) {
    return <DriverPage orders={orders} user={user} companies={companies} driverPayments={driverPayments} notifications={notifications} onClearNotifs={() => {}} onMarkAllRead={() => {}} onLogout={handleLogout} onSosAlert={() => {}} onUpdateUser={setUser} onUpdateOrder={(updated: any) => setOrders(prev => prev.map(o => o.id === updated.id ? updated as any : o))} />;
  }

  const handleAddUser = async (staffData: any) => {
    if (!currentCompany) throw new Error("Selecione uma unidade para gerenciar acessos.");
    const newUser = await db.registerStaff(staffData, currentCompany.id);
    setUsersList(prev => [...prev, newUser as any]);
    
    // Disparar e-mail de onboarding para o novo colaborador
    (db as any).sendWelcomeEmail?.(currentCompany, newUser, 'STAFF').catch((err: any) => {
      addNotification("Aviso", `E-mail de onboarding falhou: ${err.message}`, "warning");
      console.error("[MAILER ERROR]", err);
    });
  };

  const handleUpdateUser = async (userId: string, updates: Partial<User>) => {
    const updated = await db.updateProfile(userId, updates as any);
    setUsersList(prev => prev.map(u => u.id === userId ? updated as any : u));
    if (user.id === userId) {
      setUser(updated as any);
    }
  };

  const canAccess = (tabId: string) => {
    if (!user) return false;
    if (user.role === UserRole.SUPER_ADMIN) return true;
    
    // Se for ADMIN e não tiver abas específicas, vê tudo por padrão.
    if (user.role === UserRole.ADMIN && (!user.authorizedTabs || user.authorizedTabs.length === 0)) {
      return true;
    }

    // Se tiver abas específicas (seja ADMIN ou outro papel), verifica a inclusão.
    if (user.authorizedTabs && user.authorizedTabs.length > 0) {
      // Normaliza para evitar problemas com espaços ou capitalização
      const normalizedTabs = user.authorizedTabs.map(t => String(t).trim().toLowerCase());
      const normalizedTabId = String(tabId).trim().toLowerCase();
      
      // Sempre permite o dashboard se o usuário tiver qualquer acesso
      if (normalizedTabId === 'dashboard') return true;
      
      return normalizedTabs.includes(normalizedTabId);
    }

    // Se for um papel operacional e não tiver abas, não vê nada (comportamento esperado para segurança)
    return false;
  };

  const handleCreateLead = async (leadData: Partial<Lead>) => {
    const targetCoId = impersonatedCompanyId || user?.companyId || (user?.role === UserRole.SUPER_ADMIN ? companies[0]?.id : null);
    if (!targetCoId) {
      addNotification("Erro", "ID da empresa não encontrado. Selecione uma empresa ou aguarde a sincronização.", "error");
      return;
    }

    try {
      const payload = {
        ...leadData,
        id: leadData.id || `temp-lead-${Date.now()}`,
        companyId: targetCoId,
        assignedTo: leadData.assignedTo || user?.id || '',
        createdAt: new Date().toISOString(),
        tags: leadData.tags || [],
        value: Number(leadData.value) || 0,
        status: leadData.status || 'lead-novo'
      };

      const newLead = await db.saveLead(payload as any);
      setLeads(prev => [newLead as any, ...prev]);
      addNotification("Sucesso", "Lead criado com sucesso!", "success");
    } catch (e: any) {
      console.error("Erro ao criar lead:", e);
      addNotification("Erro", "Erro ao criar lead: " + e.message, "error");
    }
  };

  const handleConvertLead = async (leadId: string) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    try {
      const updatedLead = await db.saveLead({ ...lead, status: LeadStatus.CUSTOMER } as any);
      setLeads(prev => prev.map(l => l.id === leadId ? updatedLead as any : l));
      addNotification("Sucesso", "Lead convertido em cliente!", "success");
    } catch (e: any) {
      addNotification("Erro", "Erro ao converter lead: " + e.message, "error");
    }
  };

  const handleUpdateLead = async (leadId: string, updates: Partial<Lead>) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    try {
      const updatedLead = await db.saveLead({ ...lead, ...updates } as any);
      setLeads(prev => prev.map(l => l.id === leadId ? updatedLead as any : l));
    } catch (e: any) {
      addNotification("Erro", "Erro ao atualizar lead: " + e.message, "error");
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      await db.deleteLead(leadId);
      setLeads(prev => prev.filter(l => l.id !== leadId) as any);
      addNotification("Sucesso", "Lead removido.", "success");
    } catch (e: any) {
      addNotification("Erro", "Erro ao remover lead: " + e.message, "error");
    }
  };

  const handleUpdateKanbanColumns = async (updatedColumns: any[]) => {
    setKanbanColumns(updatedColumns);
    const targetCoId = impersonatedCompanyId || user?.companyId || (user?.role === UserRole.SUPER_ADMIN ? companies[0]?.id : null);
    if (targetCoId) {
      await (db as any).saveKanbanColumns?.(targetCoId, updatedColumns);
    }
  };

  const handleSaveTask = async (taskData: Partial<Task>) => {
    const targetCoId = impersonatedCompanyId || user?.companyId;
    if (!targetCoId) return;

    try {
      const newTask = await db.saveTask({
        ...taskData,
        id: taskData.id || `temp-task-${Date.now()}`,
        companyId: targetCoId,
        createdAt: taskData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as any);

      setTasks(prev => {
        const exists = prev.find(t => t.id === newTask.id);
        if (exists) return prev.map(t => t.id === newTask.id ? newTask as any : t);
        return [newTask as any, ...prev];
      });
      addNotification("Sucesso", "Tarefa salva com sucesso!", "success");
    } catch (e: any) {
      addNotification("Erro", "Erro ao salvar tarefa: " + e.message, "error");
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await db.deleteTask(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      addNotification("Sucesso", "Tarefa removida.", "success");
    } catch (e: any) {
      addNotification("Erro", "Erro ao remover tarefa: " + e.message, "error");
    }
  };

  const handleConvertToOrder = async (estimate: Estimate) => {
    try {
      const newOrder = await db.convertEstimateToOrder(estimate.id);
      setOrders(prev => [newOrder as any, ...prev]);
      // Update local estimates state
      setEstimates(prev => prev.map(e => e.id === estimate.id ? { ...e, status: 'ACEITO' as any } : e));
      addNotification("Sucesso", "Orçamento convertido em pedido!", "success");
      setActiveTab('orders');
      setSelectedOrder(newOrder as any);
    } catch (e: any) {
      addNotification("Erro", "Erro ao converter orçamento: " + e.message, "error");
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'master-dash': return <SuperAdminDashboard companies={companies} onNewCompany={() => setIsNewCompanyModalOpen(true)} onNavigate={setActiveTab} onToggleBlock={handleToggleBlock} onRefresh={() => user && syncUnitData(user.id, user.companyId)} />;
      case 'companies': return <CompaniesManagement companies={companies} onNewCompany={() => setIsNewCompanyModalOpen(true)} onToggleNfse={handleToggleNfse} onToggleBlock={handleToggleBlock} onDeleteCompany={handleDeleteCompany} onManageLicense={handleManageLicense} onImpersonate={setImpersonatedCompanyId} />;
      case 'global-finance': return <GlobalFinance companies={companies} onNewTransaction={() => setIsNewTxModalOpen(true)} />;
      case 'core-api': return <IntegrationCore />;
      case 'activity-log': return <ActivityLogPage users={users_list} />;
      case 'dashboard': return <DashboardPage orders={orders} products={products} notifications={notifications} goals={goals} onSelectOrder={setSelectedOrder} onNavigate={setActiveTab} />;
      case 'monitor': return <LogisticsMonitorPage orders={orders} clients={clients} onSelectOrder={setSelectedOrder} onExit={() => setActiveTab('dashboard')} />;
      case 'leads': return (
        <LeadsPage 
          users={users_list} 
          leads={leads} 
          columns={kanbanColumns}
          onCreateLead={handleCreateLead}
          onUpdateLead={handleUpdateLead}
          onDeleteLead={handleDeleteLead}
          onUpdateColumns={handleUpdateKanbanColumns}
          onSelectLead={setSelectedLead} 
          onConvertLead={handleConvertLead} 
          onStartProposal={() => setActiveTab('estimates')} 
        />
      );
      case 'estimates': return <EstimatesPage estimates={estimates} clients={clients} products={products} onAddEstimate={(e: any) => setEstimates([...estimates, e])} onConvertToOrder={handleConvertToOrder} />;
      case 'contracts': return <ContractsPage clients={clients} contracts={contracts} onUpdateContracts={setContracts} />;
      case 'projects': return <ProjectsPage clients={clients} users={users_list} currentUser={user} projects={projects} tasks={tasks} onUpdateProjects={setProjects} onAddTask={handleSaveTask} onUpdateTask={handleSaveTask} onDeleteTask={handleDeleteTask} onNavigate={setActiveTab} />;
      case 'tasks': return <TasksPage tasks={tasks} projects={projects} users={users_list} onAddTask={handleSaveTask} onUpdateTask={handleSaveTask} onDeleteTask={handleDeleteTask} />;
      case 'goals': return <GoalsPage goals={goals} onUpdateGoals={setGoals} orders={orders} />;
      case 'calendar': return <CalendarPage orders={orders} leads={leads} onSelectOrder={setSelectedOrder} onSelectLead={() => {}} />;
      case 'orders': return <OrdersPage orders={orders} clients={clients} companies={companies} onSelectOrder={setSelectedOrder} onNewOrder={() => setActiveTab('estimates')} user={user} />;
      case 'separation': return <SeparationPage orders={orders} clients={clients} onUpdateStatus={(id: string, s: any) => setOrders(prev => prev.map(o => o.id === id ? {...o, status: s} : o))} onSelectOrder={setSelectedOrder} onLaunchTerminal={() => setIsExpeditionTerminalOpen(true)} />;
      case 'products': return <ProductsPage products={products} orders={orders} user={user} plans={plans} onAddProducts={(p) => setProducts([...products, ...p])} onUpdateProducts={setProducts} />;
      case 'teams': return <TeamsPage teams={teams} users={users_list} user={user} onUpdateTeams={setTeams} onUpdateUsers={async (updatedUsers) => {
        for (const u of updatedUsers) {
          const original = users_list.find(old => old.id === u.id);
          if (JSON.stringify(original) !== JSON.stringify(u)) {
            await handleUpdateUser(u.id, u);
          }
        }
      }} addNotification={addNotification} />;
      case 'maintenance': return <MaintenancePage products={products} onReleaseFromMaintenance={(id, qty) => setProducts(prev => prev.map(p => p.id === id ? {...p, quantityMaintenance: p.quantityMaintenance - qty} : p))} />;
      case 'quality': return <QualityAnalysisPage orders={orders} clients={clients} onApplyFee={() => {}} />;
      case 'event-finance': return <EventFinancePage orders={orders} onUpdateOrders={setOrders} clients={clients} invoices={invoices} onUpdateInvoices={setInvoices} onAddDriverPayment={(p: any) => setDriverPayments([p, ...driverPayments])} />;
      case 'event-profitability': return <EventProfitabilityPage orders={orders} clients={clients} onUpdateOrders={setOrders} />;
      case 'invoices': return <InvoicesPage clients={clients} currentUser={user} company={currentCompany} invoices={invoices} onUpdateInvoices={setInvoices} isNfseEnabled={currentCompany?.nfseEnabled} addNotification={addNotification} />;
      case 'payments': return <PaymentsPage clients={clients} orders={orders} user={user} addNotification={addNotification} />;
      case 'internal-finance': return <InternalFinancePage orders={orders} users={users_list} driverPayments={driverPayments} onAddDriverPayment={(p: any) => setDriverPayments([p, ...driverPayments])} addNotification={addNotification} />;
      case 'clients': return <ClientsPage clients={clients} orders={orders} onNewOrder={() => setActiveTab('estimates')} onSelectClient={setSelectedClient} onNewClient={() => setIsNewClientModalOpen(true)} addNotification={addNotification} />;
      case 'users': return (
        <UserManagementPage 
          users={users_list} 
          plans={plans}
          onAddUser={handleAddUser} 
          onUpdateUser={handleUpdateUser}
          addNotification={addNotification} 
          isMasterView={user.role === UserRole.SUPER_ADMIN} 
          user={user} 
          currentCompany={currentCompany} 
        />
      );
      case 'settings': return <SettingsPage currentUser={user} currentCompany={currentCompany} users={users_list} plans={plans} onUpdatePlan={(_p) => {}} onUpdateUsers={async (updatedUsers) => {
        for (const u of updatedUsers) {
          await handleUpdateUser(u.id, u);
        }
      }} onUpdateUser={(u) => handleUpdateUser(u.id, u)} onUpdateCompany={handleUpdateCompany} addNotification={addNotification} />;
      default: return <DashboardPage orders={orders} products={products} notifications={notifications} goals={goals} onSelectOrder={setSelectedOrder} onNavigate={setActiveTab} />;
    }
  };

  return (
    <div className="flex h-screen bg-[#f8f9fa] overflow-hidden font-sans">
      <aside className={`bg-secondary h-screen flex flex-col transition-all duration-500 relative z-[100] ${sidebarCollapsed ? 'w-20' : 'w-72'}`}>
        <div className="p-6 flex items-center gap-3 border-b border-white/5">
          <div className="bg-primary p-2 rounded-xl text-white shadow-lg">
            <Truck size={sidebarCollapsed ? 20 : 24} strokeWidth={2.5} />
          </div>
          {!sidebarCollapsed && <span className="text-xl font-black text-white uppercase tracking-tighter italic">EventLog <span className="text-primary">PRO</span></span>}
        </div>
        
        <div className="flex-1 overflow-y-auto py-6 px-3 scrollbar-hide space-y-1">
           {user.role === UserRole.SUPER_ADMIN && (
             <SidebarDropdown icon={ShieldCheck} label="Adm. Master" isOpen={openGroups.master} onToggle={() => toggleGroup('master')} isCollapsed={sidebarCollapsed}>
                <SidebarItem active={activeTab === 'master-dash'} icon={LayoutDashboard} label="Global Stats" isSubItem onClick={() => setActiveTab('master-dash')} />
                <SidebarItem active={activeTab === 'companies'} icon={Building2} label="Locadoras SaaS" isSubItem onClick={() => setActiveTab('companies')} />
                <SidebarItem active={activeTab === 'global-finance'} icon={DollarSign} label="Financeiro SaaS" isSubItem onClick={() => setActiveTab('global-finance')} />
                <SidebarItem active={activeTab === 'core-api'} icon={Zap} label="Core IA/API" isSubItem onClick={() => setActiveTab('core-api')} />
                <SidebarItem active={activeTab === 'activity-log'} icon={History} label="Audit Logs" isSubItem onClick={() => setActiveTab('activity-log')} />
             </SidebarDropdown>
           )}

           <div className="h-[1px] bg-white/5 mx-4 my-2 opacity-50"></div>
           
           {canAccess('dashboard') && (
             <SidebarItem active={activeTab === 'dashboard'} icon={Activity} label="Painel Geral" isCollapsed={sidebarCollapsed} onClick={() => setActiveTab('dashboard')} />
           )}
           
           {canAccess('monitor') && (
             <SidebarItem active={activeTab === 'monitor'} icon={Radio} label="Radar Live GPRS" isCollapsed={sidebarCollapsed} onClick={() => setActiveTab('monitor')} />
           )}

           {['leads', 'estimates', 'contracts', 'projects', 'tasks', 'goals'].some(canAccess) && (
             <SidebarDropdown icon={Target} label="Comercial" isOpen={openGroups.comercial} onToggle={() => toggleGroup('comercial')} isCollapsed={sidebarCollapsed}>
                {canAccess('leads') && <SidebarItem active={activeTab === 'leads'} icon={Zap} label="Leads CRM" isSubItem onClick={() => setActiveTab('leads')} />}
                {canAccess('estimates') && <SidebarItem active={activeTab === 'estimates'} icon={Wallet} label="Orçamentos" isSubItem onClick={() => setActiveTab('estimates')} />}
                {canAccess('contracts') && <SidebarItem active={activeTab === 'contracts'} icon={FileSignature} label="Contratos" isSubItem onClick={() => setActiveTab('contracts')} />}
                {canAccess('projects') && <SidebarItem active={activeTab === 'projects'} icon={FolderKanban} label="Projetos" isSubItem onClick={() => setActiveTab('projects')} />}
                {canAccess('tasks') && <SidebarItem active={activeTab === 'tasks'} icon={ClipboardList} label="Tarefas" isSubItem onClick={() => setActiveTab('tasks')} />}
                {canAccess('goals') && <SidebarItem active={activeTab === 'goals'} icon={TrendingUp} label="Metas Vendas" isSubItem onClick={() => setActiveTab('goals')} />}
             </SidebarDropdown>
           )}

           {['calendar', 'orders', 'separation', 'products', 'teams', 'maintenance', 'quality'].some(canAccess) && (
             <SidebarDropdown icon={Truck} label="Logística" isOpen={openGroups.logistica} onToggle={() => toggleGroup('logistica')} isCollapsed={sidebarCollapsed}>
                {canAccess('calendar') && <SidebarItem active={activeTab === 'calendar'} icon={CalendarIcon} label="Agenda" isSubItem onClick={() => setActiveTab('calendar')} />}
                {canAccess('orders') && <SidebarItem active={activeTab === 'orders'} icon={ClipboardList} label="Ordens O.S." isSubItem onClick={() => setActiveTab('orders')} />}
                {canAccess('separation') && <SidebarItem active={activeTab === 'separation'} icon={Package} label="Expedição" isSubItem onClick={() => setActiveTab('separation')} />}
                {canAccess('products') && <SidebarItem active={activeTab === 'products'} icon={PackageSearch} label="Inventário" isSubItem onClick={() => setActiveTab('products')} />}
                {canAccess('teams') && <SidebarItem active={activeTab === 'teams'} icon={Users} label="Equipes" isSubItem onClick={() => setActiveTab('teams')} />}
                {canAccess('maintenance') && <SidebarItem active={activeTab === 'maintenance'} icon={Wrench} label="Oficina" isSubItem onClick={() => setActiveTab('maintenance')} />}
                {canAccess('quality') && <SidebarItem active={activeTab === 'quality'} icon={ShieldCheck} label="IA Avarias" isSubItem onClick={() => setActiveTab('quality')} />}
             </SidebarDropdown>
           )}

           {['event-finance', 'event-profitability', 'invoices', 'payments', 'internal-finance'].some(canAccess) && (
             <SidebarDropdown icon={DollarSign} label="Financeiro" isOpen={openGroups.financeiro} onToggle={() => toggleGroup('financeiro')} isCollapsed={sidebarCollapsed}>
                {canAccess('event-finance') && <SidebarItem active={activeTab === 'event-finance'} icon={ShieldCheck} label="Controladoria" isSubItem onClick={() => setActiveTab('event-finance')} />}
                {canAccess('event-profitability') && <SidebarItem active={activeTab === 'event-profitability'} icon={BarChart3} label="Lucro Evento" isSubItem onClick={() => setActiveTab('event-profitability')} />}
                {canAccess('invoices') && <SidebarItem active={activeTab === 'invoices'} icon={FileText} label="Faturas / NF" isSubItem onClick={() => setActiveTab('invoices')} />}
                {canAccess('payments') && <SidebarItem active={activeTab === 'payments'} icon={CreditCard} label="Pagamentos" isSubItem onClick={() => setActiveTab('payments')} />}
                {canAccess('internal-finance') && <SidebarItem active={activeTab === 'internal-finance'} icon={Activity} label="Fluxo Caixa" isSubItem onClick={() => setActiveTab('internal-finance')} />}
             </SidebarDropdown>
           )}

           {['clients', 'users', 'settings'].some(canAccess) && (
             <SidebarDropdown icon={SettingsIcon} label="Sistema" isOpen={openGroups.sistema} onToggle={() => toggleGroup('sistema')} isCollapsed={sidebarCollapsed}>
                {canAccess('clients') && <SidebarItem active={activeTab === 'clients'} icon={Building2} label="Parceiros" isSubItem onClick={() => setActiveTab('clients')} />}
                {canAccess('users') && <SidebarItem active={activeTab === 'users'} icon={Key} label="Acessos" isSubItem onClick={() => setActiveTab('users')} />}
                {canAccess('settings') && <SidebarItem active={activeTab === 'settings'} icon={SettingsIcon} label="Configurações" isSubItem onClick={() => setActiveTab('settings')} />}
             </SidebarDropdown>
           )}
        </div>

        <div className="p-4 border-t border-white/5 space-y-2">
           <SidebarItem icon={LogOut} label="Encerrar Sessão" isCollapsed={sidebarCollapsed} onClick={handleLogout} />
           <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="w-full flex items-center justify-center p-3 text-white/20 hover:text-white transition-colors">{sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}</button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-screen relative overflow-hidden bg-[#fcfdfe]">
        {impersonatedCompanyId && (
          <div className="bg-amber-500 text-white px-8 py-2 flex justify-between items-center z-[110] shadow-lg">
             <div className="flex items-center gap-3">
                <ShieldCheck size={18} className="animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-widest">Modo Visão Ativo: {currentCompany?.name}</span>
             </div>
             <button onClick={() => setImpersonatedCompanyId(null)} className="bg-white/20 hover:bg-white/30 px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-2 transition-all"><EyeOff size={14} /> Sair</button>
          </div>
        )}

        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-8 shrink-0 relative z-50">
           <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center overflow-hidden rounded-full bg-primary/5 border border-primary/10">
                 <InteractiveGlobe size={80} className="opacity-80" />
              </div>
              <h2 className="text-sm font-black uppercase text-secondary tracking-widest italic flex items-center gap-2">
                {activeTab.replace('-', ' ')}
              </h2>
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/5 border border-emerald-500/10 text-[9px] font-black uppercase tracking-widest text-emerald-600">
                 <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                 Sistemas Online
              </div>
           </div>
           <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                 <p className="text-sm font-black text-secondary uppercase leading-none">{user.name}</p>
                 <p className="text-[10px] font-bold text-primary uppercase tracking-widest mt-1 italic">{user.role.replace('_', ' ')}</p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-gray-50 flex items-center justify-center font-black text-primary border-2 border-gray-100 overflow-hidden shadow-sm">{user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : user.name.charAt(0)}</div>
           </div>
        </header>

        <div className="flex-1 overflow-y-auto scrollbar-hide bg-[#fcfdfe]">{renderContent()}</div>
      </main>
      
      {/* Toast Notifications Container */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 z-[3000] w-full max-w-sm pointer-events-none">
        {notifications.map(notif => (
          <div key={notif.id} className="pointer-events-auto mb-2 first:mt-6">
            <ToastNotification 
              title={notif.title} 
              message={notif.message} 
              type={notif.type} 
              onClose={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))} 
            />
          </div>
        ))}
      </div>

      {isExpeditionTerminalOpen && <ExpeditionTerminalPage orders={orders} clients={clients} onClose={() => setIsExpeditionTerminalOpen(false)} />}
      {isNewTxModalOpen && <NewTransactionModal companies={companies} onClose={() => setIsNewTxModalOpen(false)} onSubmit={handleNewTransaction} />}
      {isNewCompanyModalOpen && <NewCompanyModal onClose={() => setIsNewCompanyModalOpen(false)} onSubmit={handleCreateCompany} />}
      {isNewClientModalOpen && <NewClientModal onClose={() => setIsNewClientModalOpen(false)} onSubmit={handleCreateClient} addNotification={addNotification} />}
      {selectedOrder && <OrderDetailsPanel order={selectedOrder} clients={clients} teams={teams} onClose={() => setSelectedOrder(null)} user={user} onAssignTeam={()=>{}} onUpdateStatus={()=>{}} />}
      {selectedClient && <ClientDetailsPanel client={selectedClient} orders={orders} onClose={() => setSelectedClient(null)} onEdit={() => {}} onSelectOrder={setSelectedOrder} />}
      {selectedLead && (
        <LeadDetailsModal 
          lead={selectedLead} 
          onClose={() => setSelectedLead(null)} 
          onConvert={() => handleConvertLead(selectedLead.id)} 
          onStartProposal={() => {
            setSelectedLead(null);
            setActiveTab('estimates');
          }}
          onUpdateNotes={(notes: string) => handleUpdateLead(selectedLead.id, { description: notes })}
          users={users_list as any}
        />
      )}

      {syncError && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-red-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 animate-bounce">
          <div className="p-2 bg-white/20 rounded-lg">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="font-bold">Erro de Sincronização</p>
            <p className="text-sm opacity-90">{syncError}</p>
          </div>
          <button 
            onClick={() => user && syncUnitData(user.id, user.companyId)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Tentar novamente"
          >
            <RefreshCw className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {isSyncing && !syncError && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">Sincronizando...</span>
        </div>
      )}
    </div>
  );
}

export default App;
